import { AnalogTemperatureSensor, Author, BoilerAdapter, getAuthor, HeatingCircuit, IntervalTimetable, Notification, NTCTemperatureCurve, Pza, TimeInterval, WiredTemperatureSensor, } from 'z3k-config';
import { SettingsMasterStep } from 'entities/settings-master/model/type';
import { ats_air, ats_water, dhw_timeinterval, hc_consumer, hc_dhw, hm_summer, notification_boiler_error, nts_curve, pza8194, timetable_dhw, } from 'features/settings-master/model/default/objects';
import { ats_air_id, ats_water_id, consumer_ti_id, consumer_timetable_id, dhw_ti_id, dhw_timetable_id, hc_boiler_id, hc_dhw_id, hc_id, hm_comfort_id, hm_economy_id, hm_off_id, hm_schedule_id, hm_summer_id, ntc_curve_id, relay_id, } from 'features/settings-master/model/default/ids';
import { findAndRemoveMutableObjects, findOrCreateBaseObjects, } from 'features/settings-master/lib/find-and-remove-objects';
import { assertNever, c2k, g2dk } from 'utils';
import { getDeviceSpec } from 'z3k-parser/model/device-spec/z3k-feature-flags';
import { addInternetTemp } from 'z3k-settings/utils/config';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
function addWaterSensor(z3k_config, waterSensorId) {
    [waterSensorId, z3k_config] = AnalogTemperatureSensor.get.add(z3k_config, ats_water);
    return [waterSensorId, z3k_config];
}
export function changeConfigBySettingsMaster(dataSelectSettings, config, selectedSettings, is_configured_by_wizard) {
    let newConfig = { ...config };
    const spec = getDeviceSpec(config.device.name, config.device.hardware);
    // remove all automatically added wired sensors which wasn't mapped to real sensors yet
    newConfig = WiredTemperatureSensor.get.remove(newConfig, (sensor) => sensor.serial.length == 1);
    newConfig = AnalogTemperatureSensor.get.remove(newConfig, (sensor) => getAuthor(sensor.id) === Author.PREDEFINED);
    newConfig = findAndRemoveMutableObjects(findOrCreateBaseObjects(newConfig));
    if (!is_configured_by_wizard) {
        newConfig = { ...newConfig, wired_temperature_sensors: [] };
    }
    let isRelay = false;
    let airSensorId = ats_air_id;
    const newAirSensorId = dataSelectSettings?.airTempSensor?.id;
    let waterSensorId = ats_water_id;
    //для аналоговых датчиков температуры нужны таблицы пересчёта
    //need for fix bug - if many times use MasterSettings - device have many curves
    newConfig = NTCTemperatureCurve.get.removeById(newConfig, ntc_curve_id);
    newConfig = NTCTemperatureCurve.get.createIfNotExists(newConfig, nts_curve);
    let pzaSensorId = Pza.get.one(newConfig, () => true)?.id;
    const newXYforPZA = dataSelectSettings?.pza;
    const pza = Pza.get.one(newConfig, () => true);
    newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_id, {
        heat_request: hc_consumer.heat_request,
    });
    let boiler_adapter_id = null;
    const wired_temperature_sensor_id = config
        .wired_temperature_sensors?.length
        ? config.wired_temperature_sensors[0].id
        : null;
    const existing_boiler_adapter = BoilerAdapter.get.one(newConfig, () => true);
    boiler_adapter_id = existing_boiler_adapter?.id ?? null;
    for (const name of selectedSettings) {
        switch (name) {
            case SettingsMasterStep.Relay: {
                isRelay = true;
                newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_boiler_id, {
                    water_min_temperature: 15,
                    water_temp_sensor: null,
                    water_temp_sensor_reserve: null,
                    hysteresis: 3,
                    control_devices: [relay_id],
                });
                break;
            }
            case SettingsMasterStep.DigitalBus: {
                if (boiler_adapter_id == null) {
                    throw new Error('Невозможно сохранить настройки. Не найден адаптер цифровой шины!');
                }
                if (dataSelectSettings?.boiler_model?.value) {
                    newConfig = BoilerAdapter.get.applySetterById(newConfig, boiler_adapter_id, {
                        name: 'АЦШ',
                        max_modulation_level: 100,
                        boiler_model: dataSelectSettings?.boiler_model?.value,
                        ...(dataSelectSettings?.boiler_adapter?.value && {
                            type: dataSelectSettings?.boiler_adapter?.value,
                        }),
                    });
                }
                else {
                    newConfig = BoilerAdapter.get.applySetterById(newConfig, boiler_adapter_id, { name: 'Котёл' });
                }
                [, newConfig] = Notification.get.add(newConfig, notification_boiler_error);
                newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_boiler_id, {
                    water_min_temperature: 15,
                    water_temp_sensor: boiler_adapter_id,
                    water_temp_sensor_reserve: null,
                    hysteresis: 1,
                    control_devices: [boiler_adapter_id],
                });
                break;
            }
            case SettingsMasterStep.ElectricalBoiler: {
                newConfig = BoilerAdapter.get.modifyById(newConfig, boiler_adapter_id, (ba) => ({
                    ...BoilerAdapter.setExternalPanel(ba, false),
                }));
                newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_boiler_id, {
                    water_min_temperature: 10,
                });
                newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_id, {
                    water_min_temperature: 10,
                });
                break;
            }
            case SettingsMasterStep.BoilerDHW: {
                newConfig = HeatingCircuit.get.createIfNotExists(newConfig, hc_dhw);
                if (dataSelectSettings?.boiler_model?.value) {
                    newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_dhw_id, {
                        dhw_temp_sensor: boiler_adapter_id,
                        control_devices: [boiler_adapter_id],
                        hysteresis: 0.5,
                        heat_request: HeatingCircuit.HEAT_REQUEST__MAX_BOILER_TEMP,
                    });
                }
                else {
                    newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_dhw_id, {
                        dhw_temp_sensor: boiler_adapter_id,
                        control_devices: [boiler_adapter_id],
                    });
                }
                newConfig = IntervalTimetable.get.createIfNotExists(newConfig, timetable_dhw);
                newConfig = TimeInterval.get.createIfNotExists(newConfig, dhw_timeinterval);
                newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_boiler_id, {
                    water_min_temperature: 0,
                });
                newConfig = HeatingMode.get.createIfNotExists(newConfig, hm_summer);
                const circuitId2temperatureSetting = {
                    [hm_comfort_id]: c2k(50),
                    [hm_economy_id]: c2k(30),
                    [hm_schedule_id]: dhw_timetable_id,
                    [hm_off_id]: HeatingMode.OFF_MODE,
                    [hm_summer_id]: c2k(50),
                };
                newConfig = HeatingMode.get.modify(newConfig, (hm) => circuitId2temperatureSetting[hm.id] != undefined, (hm) => ({
                    ...hm,
                    heating_zones: [
                        ...hm.heating_zones.filter((hz) => hz.heating_circuit != hc_dhw_id),
                        {
                            heating_circuit: hc_dhw_id,
                            temperature_setting: circuitId2temperatureSetting[hm.id],
                            adjusting_sensor: null,
                            pza: null,
                        },
                    ],
                }));
                newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_dhw_id, {
                    setting_register: 0,
                });
                break;
            }
            case SettingsMasterStep.DoubleCircuit: {
                newConfig = HeatingCircuit.get.modifyById(newConfig, hc_dhw_id, (hc) => HeatingCircuit.setFlowDHW(hc, true));
                break;
            }
            case SettingsMasterStep.AirControl: {
                if (dataSelectSettings?.airTempSensor === undefined) {
                    [airSensorId, newConfig] = AnalogTemperatureSensor.get.add(newConfig, ats_air);
                }
                if (newAirSensorId) {
                    newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_id, {
                        air_temp_sensor: newAirSensorId,
                    });
                }
                const consumer2temperatureSetting = {
                    [hm_comfort_id]: c2k(24),
                    [hm_economy_id]: c2k(15),
                    [hm_schedule_id]: consumer_timetable_id,
                    [hm_off_id]: HeatingMode.OFF_MODE,
                    [hm_summer_id]: HeatingMode.OFF_MODE,
                };
                newConfig = HeatingMode.get.modify(newConfig, (hm) => consumer2temperatureSetting[hm.id] != undefined, (hm) => ({
                    ...hm,
                    heating_zones: [
                        ...hm.heating_zones.filter((hz) => hz.heating_circuit != hc_id),
                        {
                            heating_circuit: hc_id,
                            temperature_setting: consumer2temperatureSetting[hm.id],
                            adjusting_sensor: null,
                            pza: null,
                        },
                    ],
                }));
                const dhw2temperatureSetting = {
                    [hm_comfort_id]: c2k(50),
                    [hm_economy_id]: c2k(36),
                    [hm_schedule_id]: dhw_timetable_id,
                    [hm_off_id]: HeatingMode.OFF_MODE,
                    [hm_summer_id]: c2k(50),
                };
                newConfig = HeatingMode.get.modify(newConfig, (hm) => dhw2temperatureSetting[hm.id] != undefined, (hm) => ({
                    ...hm,
                    heating_zones: hm.heating_zones.some(({ heating_circuit }) => heating_circuit === hc_dhw_id)
                        ? [
                            ...hm.heating_zones.filter((hz) => hz.heating_circuit != hc_dhw_id),
                            {
                                heating_circuit: hc_dhw_id,
                                temperature_setting: dhw2temperatureSetting[hm.id],
                                adjusting_sensor: null,
                                pza: null,
                            },
                        ]
                        : hm.heating_zones,
                }));
                if (isRelay) {
                    newConfig = HeatingCircuit.get.modifyById(newConfig, hc_id, (hc) => ({
                        ...HeatingCircuit.setAlwaysHeatRequest(hc, true),
                    }));
                }
                newConfig = IntervalTimetable.get.applySetterById(newConfig, consumer_timetable_id, {
                    outside_default: c2k(18),
                });
                newConfig = IntervalTimetable.get.applySetterById(newConfig, dhw_timetable_id, {
                    outside_default: c2k(20),
                });
                newConfig = TimeInterval.get.applySetterById(newConfig, consumer_ti_id, {
                    temperature: c2k(22),
                });
                newConfig = TimeInterval.get.applySetterById(newConfig, dhw_ti_id, {
                    temperature: c2k(45),
                });
                newConfig = Pza.get.remove(newConfig, () => true);
                break;
            }
            case SettingsMasterStep.WaterControl: {
                if (isRelay) {
                    [waterSensorId, newConfig] = addWaterSensor(newConfig, waterSensorId);
                }
                newConfig = HeatingCircuit.get.modifyById(newConfig, hc_id, (hc) => ({
                    ...HeatingCircuit.setAlwaysHeatRequest(HeatingCircuit.setConsumerMode(hc, HeatingCircuit.ConsumerMode.WATER), true),
                    water_min_temperature: 20,
                    water_temp_sensor: isRelay
                        ? waterSensorId != null
                            ? waterSensorId
                            : null //чтобы успокоить TS
                        : boiler_adapter_id,
                    water_temp_sensor_reserve: null,
                    hysteresis: 2,
                    pza: 0,
                    position: 2,
                    air_temp_sensor: null,
                    air_temp_sensor_reserve: null,
                }));
                const consumer2temperatureSetting = {
                    [hm_comfort_id]: c2k(60),
                    [hm_economy_id]: c2k(45),
                    [hm_schedule_id]: consumer_timetable_id,
                    [hm_off_id]: HeatingMode.OFF_MODE,
                    [hm_summer_id]: HeatingMode.OFF_MODE,
                };
                newConfig = HeatingMode.get.modify(newConfig, (hm) => consumer2temperatureSetting[hm.id] != undefined, (hm) => ({
                    ...hm,
                    heating_zones: [
                        ...hm.heating_zones.filter((hz) => hz.heating_circuit != hc_id),
                        {
                            heating_circuit: hc_id,
                            temperature_setting: consumer2temperatureSetting[hm.id],
                            adjusting_sensor: null,
                            pza: null,
                        },
                    ],
                }));
                const dhw2temperatureSetting = {
                    [hm_comfort_id]: c2k(50),
                    [hm_economy_id]: c2k(36),
                    [hm_schedule_id]: dhw_timetable_id,
                    [hm_off_id]: HeatingMode.OFF_MODE,
                    [hm_summer_id]: c2k(50),
                };
                newConfig = HeatingMode.get.modify(newConfig, (hm) => dhw2temperatureSetting[hm.id] != undefined, (hm) => ({
                    ...hm,
                    heating_zones: hm.heating_zones.some(({ heating_circuit }) => heating_circuit === hc_dhw_id)
                        ? [
                            ...hm.heating_zones.filter((hz) => hz.heating_circuit != hc_dhw_id),
                            {
                                heating_circuit: hc_dhw_id,
                                temperature_setting: dhw2temperatureSetting[hm.id],
                                adjusting_sensor: null,
                                pza: null,
                            },
                        ]
                        : hm.heating_zones,
                }));
                newConfig = IntervalTimetable.get.applySetterById(newConfig, consumer_timetable_id, {
                    outside_default: c2k(50),
                });
                newConfig = IntervalTimetable.get.applySetterById(newConfig, dhw_timetable_id, {
                    outside_default: c2k(36),
                });
                newConfig = TimeInterval.get.applySetterById(newConfig, consumer_ti_id, {
                    temperature: c2k(60),
                });
                newConfig = TimeInterval.get.applySetterById(newConfig, dhw_ti_id, {
                    temperature: c2k(45),
                });
                newConfig = Pza.get.remove(newConfig, () => true);
                break;
            }
            case SettingsMasterStep.PZAControl: {
                if (isRelay) {
                    [waterSensorId, newConfig] = addWaterSensor(newConfig, waterSensorId);
                }
                if (dataSelectSettings?.pzaControl === undefined ||
                    dataSelectSettings?.pzaControl === 'internet_temp') {
                    newConfig = addInternetTemp(newConfig);
                }
                const deleteStreetSensorBoilerAdapter = () => {
                    if (BoilerAdapter.get.where(config, BoilerAdapter.isOutside)
                        .length > 0 &&
                        boiler_adapter_id) {
                        newConfig = BoilerAdapter.get.applySetterById(newConfig, boiler_adapter_id, BoilerAdapter.setWeather(BoilerAdapter.get.where(config, BoilerAdapter.isOutside)[0], false));
                    }
                };
                const deleteStreetSensorWired = () => {
                    if (WiredTemperatureSensor.getSensors(config).filter(WiredTemperatureSensor.isOutside).length > 0 &&
                        wired_temperature_sensor_id) {
                        newConfig = WiredTemperatureSensor.get.applySetterById(newConfig, wired_temperature_sensor_id, WiredTemperatureSensor.setWeatherWired(WiredTemperatureSensor.getSensors(config).filter(WiredTemperatureSensor.isOutside)[0], false));
                    }
                };
                if (dataSelectSettings?.pzaControl === 'boiler_adapter') {
                    if (boiler_adapter_id) {
                        const boilerAdapter = BoilerAdapter.get.byId(config, boiler_adapter_id);
                        deleteStreetSensorWired();
                        if (boilerAdapter) {
                            newConfig = BoilerAdapter.get.applySetterById(newConfig, boiler_adapter_id, BoilerAdapter.setWeather(boilerAdapter, true));
                        }
                    }
                    else {
                        {
                            newConfig = addInternetTemp(newConfig);
                        }
                    }
                }
                if (dataSelectSettings?.pzaControl === 'wired_temperature_sensors') {
                    if (wired_temperature_sensor_id) {
                        const wiredSensor = WiredTemperatureSensor.get.byId(config, wired_temperature_sensor_id);
                        deleteStreetSensorBoilerAdapter();
                        if (wiredSensor) {
                            newConfig = WiredTemperatureSensor.get.applySetterById(newConfig, wired_temperature_sensor_id, WiredTemperatureSensor.setWeatherWired(wiredSensor, true));
                        }
                    }
                    else {
                        newConfig = addInternetTemp(newConfig);
                    }
                }
                if (pzaSensorId === undefined && dataSelectSettings?.pza == undefined) {
                    [pzaSensorId, newConfig] = Pza.get.add(newConfig, pza8194);
                }
                if (pzaSensorId === undefined && newXYforPZA !== null) {
                    const { x, y } = Object.fromEntries(['x', 'y'].map((key, index) => [
                        key,
                        newXYforPZA.map((temp) => g2dk(temp[index])),
                    ]));
                    const newPza = { name: 'Кривая ПЗА', x, y };
                    [pzaSensorId, newConfig] = Pza.get.add(newConfig, newPza);
                }
                if (newXYforPZA && pza) {
                    const { x, y } = Object.fromEntries(['x', 'y'].map((key, index) => [
                        key,
                        newXYforPZA.map((temp) => g2dk(temp[index])),
                    ]));
                    const newPza = { ...pza, x, y };
                    newConfig = Pza.get.replaceById(newConfig, pza.id, newPza);
                }
                newConfig = HeatingCircuit.get.modifyById(newConfig, hc_id, (hc) => ({
                    ...HeatingCircuit.setAlwaysHeatRequest(HeatingCircuit.setConsumerMode(hc, HeatingCircuit.ConsumerMode.WATER), true),
                    water_min_temperature: 20,
                    water_temp_sensor: isRelay
                        ? waterSensorId != null
                            ? waterSensorId
                            : null //чтобы успокоить TS
                        : boiler_adapter_id,
                    water_temp_sensor_reserve: null,
                    hysteresis: 2,
                    pza: pzaSensorId,
                    position: 2,
                }));
                //В режимах должно быть 20 градусов, т.к. ПЗА рассчитывается при +20
                const consumer2temperatureSetting = {
                    [hm_comfort_id]: c2k(20),
                    [hm_economy_id]: c2k(15),
                    [hm_schedule_id]: consumer_timetable_id,
                    [hm_off_id]: HeatingMode.OFF_MODE,
                    [hm_summer_id]: HeatingMode.OFF_MODE,
                };
                newConfig = HeatingMode.get.modify(newConfig, (hm) => consumer2temperatureSetting[hm.id] != undefined, (hm) => ({
                    ...hm,
                    heating_zones: [
                        ...hm.heating_zones.filter((hz) => hz.heating_circuit != hc_id),
                        {
                            heating_circuit: hc_id,
                            temperature_setting: consumer2temperatureSetting[hm.id],
                            adjusting_sensor: null,
                            pza: null,
                        },
                    ],
                }));
                break;
            }
            case SettingsMasterStep.WithPIDRegulation: {
                newConfig = HeatingCircuit.get.modifyById(newConfig, hc_id, (hc) => ({
                    ...HeatingCircuit.setAlwaysHeatRequest(HeatingCircuit.setConsumerMode(hc, HeatingCircuit.ConsumerMode.AIR_PID), true),
                    air_temp_sensor: newAirSensorId
                        ? newAirSensorId
                        : airSensorId != null
                            ? airSensorId
                            : null,
                    water_temp_sensor: isRelay
                        ? waterSensorId != null
                            ? waterSensorId
                            : null //чтобы успокоить TS
                        : boiler_adapter_id,
                    water_temp_sensor_reserve: null,
                    hysteresis: 2,
                    pza: 0,
                    position: 2,
                }));
                break;
            }
            case SettingsMasterStep.WithoutPIDRegulation: {
                newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_id, {
                    air_temp_sensor: newAirSensorId
                        ? newAirSensorId
                        : airSensorId != null
                            ? airSensorId
                            : null,
                    water_temp_sensor_reserve: null,
                    hysteresis: 0.5,
                    setting_register: 0,
                    turn_off_delay: 300,
                    pza: 0,
                    position: 2,
                    heat_request: HeatingCircuit.HEAT_REQUEST__MAX_BOILER_TEMP,
                });
                break;
            }
            case SettingsMasterStep.InRoomTemp: {
                [airSensorId, newConfig] = AnalogTemperatureSensor.get.add(newConfig, ats_air);
                newConfig = HeatingCircuit.get.modifyById(newConfig, hc_id, (hc) => ({
                    ...HeatingCircuit.setAlwaysHeatRequest(HeatingCircuit.setConsumerMode(hc, HeatingCircuit.ConsumerMode.AIR_PID), true),
                    air_temp_sensor: newAirSensorId
                        ? newAirSensorId
                        : airSensorId != null
                            ? airSensorId
                            : null,
                    water_temp_sensor: boiler_adapter_id,
                    water_temp_sensor_reserve: null,
                    hysteresis: 0.5,
                    pza: pzaSensorId,
                    position: 2,
                }));
                break;
            }
            case SettingsMasterStep.OutRoomTemp: {
                //need for not use air temp sensor
                newConfig = HeatingCircuit.get.applySetterById(newConfig, hc_id, {
                    air_temp_sensor: null,
                });
                newConfig = IntervalTimetable.get.applySetterById(newConfig, consumer_timetable_id, {
                    outside_default: c2k(18),
                });
                newConfig = TimeInterval.get.applySetterById(newConfig, consumer_ti_id, {
                    temperature: c2k(24),
                });
                break;
            }
            case SettingsMasterStep.GasBoiler:
            case SettingsMasterStep.BoilerModel:
            case SettingsMasterStep.BoilerNoDHW:
            case SettingsMasterStep.NoSavedSetting:
            case SettingsMasterStep.SavedSetting:
            case SettingsMasterStep.SingleCircuit:
                break;
            default:
                assertNever(name);
        }
    }
    return newConfig;
}
